import { jsPDF } from 'jspdf';

const BRAND = 'Sawtna';

function addWrappedText(doc, text, x, y, maxWidth, lineHeight = 5) {
  const lines = doc.splitTextToSize(text || '', maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function ensureSpace(doc, y, needed = 20) {
  if (y + needed > 275) {
    doc.addPage();
    return 20;
  }
  return y;
}

function renderListSection(doc, title, items, margin, maxWidth, y) {
  if (!items?.length) return y;

  y = ensureSpace(doc, y, 15);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 64, 175);
  y = addWrappedText(doc, title, margin, y, maxWidth) + 2;
  doc.setFont(undefined, 'normal');
  doc.setTextColor(40, 40, 40);
  items.forEach((item) => {
    y = ensureSpace(doc, y, 10);
    y = addWrappedText(doc, `• ${item}`, margin + 2, y, maxWidth - 2) + 2;
  });
  return y + 2;
}

function renderOutstandingTaskChecklist(doc, draft, margin, maxWidth, y) {
  const details =
    draft.outstanding_task_details?.length
      ? draft.outstanding_task_details
      : (draft.outstanding_tasks || []).map((title) => ({
          title,
          wasOverdue: false,
          dueTime: null,
        }));
  if (!details.length) return y;

  y = ensureSpace(doc, y, 15);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(124, 58, 237);
  y = addWrappedText(doc, `Outstanding Tasks (${details.length})`, margin, y, maxWidth) + 2;
  doc.setFont(undefined, 'normal');
  doc.setTextColor(40, 40, 40);

  details.forEach((item) => {
    y = ensureSpace(doc, y, 14);
    doc.setDrawColor(160, 160, 160);
    doc.rect(margin + 1, y - 3.5, 3.5, 3.5);
    y = addWrappedText(doc, item.title, margin + 7, y, maxWidth - 7);
    if (item.wasOverdue) {
      y = addWrappedText(doc, 'Overdue', margin + 7, y, maxWidth - 7) + 1;
    }
    if (item.dueTime) {
      const dueText = new Date(item.dueTime).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      y = addWrappedText(doc, `Was due at ${dueText}`, margin + 7, y, maxWidth - 7) + 2;
    } else {
      y += 2;
    }
  });

  return y + 2;
}

function renderPatientHandover(doc, draft, margin, maxWidth, pageWidth, nurse, shiftDate, isDay, isFirstPatient) {
  if (!isFirstPatient) {
    doc.addPage();
  }

  let y = 20;

  doc.setFontSize(22);
  doc.setTextColor(30, 64, 175);
  doc.text(BRAND, margin, y);
  y += 10;

  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('Shift Handover', margin, y);
  y += 8;

  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  y = addWrappedText(doc, shiftDate, margin, y, maxWidth);
  y = addWrappedText(doc, `Shift: ${isDay ? 'Day' : 'Night'}`, margin, y, maxWidth);
  y = addWrappedText(doc, `Nurse: ${nurse?.name || nurse?.nameEn || '—'}`, margin, y, maxWidth);
  y = addWrappedText(doc, `Hospital: ${nurse?.hospital || '—'}`, margin, y, maxWidth);
  y += 4;

  doc.setDrawColor(30, 64, 175);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;

  doc.setFontSize(13);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  y = addWrappedText(doc, draft.patientName, margin, y, maxWidth);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  y = addWrappedText(doc, `Room ${draft.patientRoom || '—'}`, margin, y, maxWidth);
  y += 6;

  const narrative =
    (typeof draft.narrative === 'string' && draft.narrative.trim())
    || [
      draft.sbar?.situation,
      draft.sbar?.background,
      draft.sbar?.assessment,
      draft.sbar?.recommendation,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join('\n\n')
    || '—';

  y = ensureSpace(doc, y, 20);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(40, 40, 40);
  y = addWrappedText(doc, narrative, margin, y, maxWidth) + 6;

  y = renderOutstandingTaskChecklist(doc, draft, margin, maxWidth, y);
  y = renderListSection(doc, 'Pending Results', draft.pending_results, margin, maxWidth, y);
  y = renderListSection(
    doc,
    'Medications Due Next Shift',
    draft.medications_due_next_shift,
    margin,
    maxWidth,
    y
  );

  doc.setDrawColor(200, 200, 200);
  doc.line(margin, 270, pageWidth - margin, 270);
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text('Reviewed and approved by: _______________________', margin, 280);
}

export function generateShiftHandoverPDF({ drafts, nurse, shiftDate, isDay }) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - margin * 2;

  if (!drafts?.length) {
    let y = 20;
    doc.setFontSize(22);
    doc.setTextColor(30, 64, 175);
    doc.text(BRAND, margin, y);
    y += 12;
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    addWrappedText(doc, 'No patients documented this shift.', margin, y, maxWidth);
  } else {
    drafts.forEach((draft, index) => {
      renderPatientHandover(doc, draft, margin, maxWidth, pageWidth, nurse, shiftDate, isDay, index === 0);
    });
  }

  const dateSlug = new Date().toISOString().slice(0, 10);
  doc.save(`Sawtna_ShiftHandover_${dateSlug}.pdf`);
}
